/**
 * Tenants Page
 * Manage tenants for the project
 */

import { useState } from 'react';
import { Card } from '@/components/common/Card';
import {
  Button,
  Input,
  Badge,
} from '@fluentui/react-components';
import {
  Add24Regular,
  Search24Regular,
  Building24Regular,
} from '@fluentui/react-icons';
import { formatCurrency, formatPercentage } from '@/utils/formatters';
import { mockTenants, type MockTenant } from '@/services/mockData';

export function Tenants() {
  const [tenants] = useState<MockTenant[]>(mockTenants);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredTenants = tenants.filter(
    (tenant) =>
      tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalNRA = tenants.reduce((sum, t) => sum + t.nra, 0);
  const leasedTenants = tenants.filter((t) => t.status === 'Leased');
  const totalLeasedNRA = leasedTenants.reduce((sum, t) => sum + t.nra, 0);
  const occupancyRate = (totalLeasedNRA / totalNRA) * 100;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Leased':
        return 'success';
      case 'Negotiating':
        return 'warning';
      case 'Prospective':
        return 'informative';
      default:
        return 'subtle';
    }
  };

  return (
    <div className="space-y-[35px]">
      {/* Header */}
      <div className="flex items-center justify-between mb-[35px]">
        <div>
          <h1 className="text-[36px] font-light text-dark-text mb-2">
            Tenant Management
          </h1>
          <p className="text-gray-300 text-sm">
            {tenants.length} total tenants • {leasedTenants.length} leased •{' '}
            {occupancyRate.toFixed(1)}% occupancy
          </p>
        </div>
        <Button appearance="primary" icon={<Add24Regular />}>
          Add Tenant
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-[30px]">
        <div className="bg-white p-[25px] rounded-lg shadow-minimal text-center">
          <div className="text-[11px] uppercase text-gray-400 tracking-[0.5px] mb-[10px] font-medium">
            Total Tenants
          </div>
          <div className="text-[28px] font-semibold text-dark-text">
            {tenants.length}
          </div>
        </div>
        <div className="bg-white p-[25px] rounded-lg shadow-minimal text-center">
          <div className="text-[11px] uppercase text-gray-400 tracking-[0.5px] mb-[10px] font-medium">
            Total Leased NRA
          </div>
          <div className="text-[28px] font-semibold text-dark-text">
            {totalLeasedNRA.toLocaleString()}
          </div>
          <div className="text-xs text-gray-300 mt-1">SF</div>
        </div>
        <div className="bg-white p-[25px] rounded-lg shadow-minimal text-center">
          <div className="text-[11px] uppercase text-gray-400 tracking-[0.5px] mb-[10px] font-medium">
            Occupancy Rate
          </div>
          <div className="text-[28px] font-semibold text-success-green">
            {occupancyRate.toFixed(1)}%
          </div>
        </div>
        <div className="bg-white p-[25px] rounded-lg shadow-minimal text-center">
          <div className="text-[11px] uppercase text-gray-400 tracking-[0.5px] mb-[10px] font-medium">
            Total Annual Rent
          </div>
          <div className="text-[28px] font-semibold text-dark-text">
            {formatCurrency(
              leasedTenants.reduce((sum, t) => sum + t.nra * t.rentPerSF, 0)
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <Card>
        <div className="flex items-center gap-4">
          <Input
            placeholder="Search tenants by name or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            contentBefore={<Search24Regular />}
            className="flex-1"
            size="large"
          />
        </div>
      </Card>

      {/* Tenants Grid */}
      <Card
        title="Tenant Directory"
        subtitle={`${filteredTenants.length} tenants`}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredTenants.map((tenant) => (
            <div
              key={tenant.id}
              className="bg-white border border-border-light rounded-lg p-5 hover:shadow-minimal transition-shadow"
            >
              {/* Icon and Badge */}
              <div className="flex items-start justify-between mb-3">
                <div className="w-12 h-12 bg-gray-20 rounded-lg flex items-center justify-center text-2xl">
                  {tenant.icon}
                </div>
                <Badge
                  appearance="tint"
                  color={getStatusColor(tenant.status)}
                  size="small"
                >
                  {tenant.status}
                </Badge>
              </div>

              {/* Tenant Info */}
              <h3 className="font-semibold text-dark-text mb-1 text-sm">
                {tenant.name}
              </h3>
              <p className="text-xs text-gray-300 mb-3">{tenant.category}</p>

              {/* Metrics */}
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-300">NRA:</span>
                  <span className="font-medium text-dark-text">
                    {tenant.nra.toLocaleString()} SF
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Rent/SF:</span>
                  <span className="font-medium text-dark-text">
                    {formatCurrency(tenant.rentPerSF)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Annual Rent:</span>
                  <span className="font-semibold text-success-green">
                    {formatCurrency(tenant.nra * tenant.rentPerSF)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-20">
                  <span className="text-gray-300">Lease Start:</span>
                  <span className="font-medium text-dark-text">
                    Month {tenant.leaseStartMonth}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredTenants.length === 0 && (
          <div className="text-center py-12">
            <Building24Regular className="mx-auto text-4xl text-gray-300 mb-3" />
            <p className="text-gray-300">No tenants found</p>
            <p className="text-sm text-gray-300 mt-1">
              Try adjusting your search criteria
            </p>
          </div>
        )}
      </Card>

      {/* Detailed Table */}
      <Card title="Lease Details" subtitle="Complete tenant information">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-20 border-b-2 border-dark-text">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                  Tenant
                </th>
                <th className="text-left px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                  Category
                </th>
                <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                  NRA (SF)
                </th>
                <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                  Rent/SF
                </th>
                <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                  Annual Rent
                </th>
                <th className="text-right px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                  Growth Rate
                </th>
                <th className="text-center px-4 py-3 font-semibold text-dark-text text-xs uppercase tracking-wide">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.map((tenant) => (
                <tr
                  key={tenant.id}
                  className="border-b border-border-light hover:bg-gray-10"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-20 rounded flex items-center justify-center text-lg">
                        {tenant.icon}
                      </div>
                      <span className="font-medium text-dark-text">
                        {tenant.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-300">{tenant.category}</td>
                  <td className="px-4 py-3 text-right text-dark-text">
                    {tenant.nra.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-dark-text">
                    {formatCurrency(tenant.rentPerSF)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-success-green">
                    {formatCurrency(tenant.nra * tenant.rentPerSF)}
                  </td>
                  <td className="px-4 py-3 text-right text-dark-text">
                    {formatPercentage(tenant.rentGrowthRate)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge
                      appearance="tint"
                      color={getStatusColor(tenant.status)}
                      size="small"
                    >
                      {tenant.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
